import { Hls, HlsVideoElement } from 'hls-video-element';
import { CastableMediaMixin } from 'castable-video/castable-mixin.js';
import { getBestCodecType, isCodecSupported } from '../utils/codec-filter.js';
import { pickStartLevelForPlayerSize } from '../utils/start-level.ts';
import { StreamDrmEngine } from '../utils/stream-drm-engine.js';
import {
  isChrome,
  isFairPlayBrowser,
  isGecko,
  isIOS,
  isSafari,
} from '../utils/browser-detection.js';

type MediaControllerElement = HTMLElement & {
  dataset: DOMStringMap;
};

type MediaSourceLike = {
  isTypeSupported(type: string): boolean;
};

type CodecFilterCandidate = {
  codecSet?: string;
  videoCodec?: string;
  audioCodec?: string;
};

type FilteredLevel = CodecFilterCandidate & {
  originalIndex?: number;
};

type HlsErrorData = {
  details?: string;
  response?: {
    code?: number;
    status?: number;
  };
  networkDetails?: {
    status?: number;
    response?: {
      status?: number;
    };
  };
  type?: string;
  fatal?: boolean;
  reason?: unknown;
  levels?: FilteredLevel[];
};

type LiveStateOptions = {
  isPlayable?: boolean;
  isLiveActive?: boolean;
  showCountdown?: boolean;
  scheduledStartTime?: string | null;
  showStatus?: boolean;
  statusMessage?: string | null;
  thumbnailUrl?: string | null;
  allowAutoResume?: boolean;
};

type LiveStatusResponse = {
  videoPlaylistUrl?: string;
  isPlayable?: boolean;
  isLiveActive?: boolean;
  showCountdown?: boolean;
  scheduledStartTime?: string | null;
  showStatus?: boolean;
  statusMessage?: string | null;
  thumbnailUrl?: string | null;
};

type RenditionMenuElement = HTMLElement & {
  filteredLevels?: FilteredLevel[];
};

// DRM-related error details from HLS.js
const DRM_ERROR_DETAILS = [
  'keySystemNoKeys',
  'keySystemNoAccess',
  'keySystemNoSession',
  'keySystemNoConfiguredLicense',
  'keySystemLicenseRequestFailed',
  'keySystemServerCertificateRequestFailed',
  'keySystemServerCertificateUpdateFailed',
  'keySystemSessionUpdateFailed',
  'keySystemStatusOutputRestricted',
  'keySystemStatusInternalError',
  'keyLoadError',
  'keyLoadTimeOut',
  'keySystemError',
];

class BunnyStreamVideoElement extends CastableMediaMixin(HlsVideoElement) {
  declare api: Hls | null;
  declare config: Record<string, unknown>;
  declare nativeEl: HTMLVideoElement;
  declare src: string;
  declare isJit: boolean;
  declare mediaSourceSupported: boolean;

  static override get observedAttributes(): string[] {
    return (super.observedAttributes || []).concat([
      'content-src',
      'disable-ios-player',
      'remember-position',
      'drm-robustness',
      'drm-version',
      'video-id',
      'library-id',
      'drm-engine',
      'drm-provider',
      'is-jit',
      'base-url',
      'level-cap',
      'start-time',
      'chromecast',
      'airplay',
      'live-status-url',
      'live-status-interval',
    ]);
  }

  _waitingKeys = false;
  _edrmEnabled = false;
  _videoId: string | null = this.getAttribute('video-id');
  _libraryId: string | null = this.getAttribute('library-id');
  _drmEngine: string | null = this.getAttribute('drm-engine');
  _drmProvider: string | null = this.getAttribute('drm-provider');
  _drmVersion: number | null = this.#parseDrmVersion(
    this.getAttribute('drm-version'),
  );
  _baseUrl = this.getAttribute('base-url') || 'https://video.bunnycdn.com';
  _levelCap = this.getAttribute('level-cap') === 'true';
  _startTime = parseFloat(this.getAttribute('start-time') || '0');
  _fragmentsLoaded = 0;
  _filteredLevels: FilteredLevel[] | null = null;
  _internalDrmInitialized = false; // Guard against double initialization
  _streamDrmEngine: StreamDrmEngine | null = null; // FairPlay DRM engine instance for internal DRM mode
  _liveStatusUrl: string | null = this.getAttribute('live-status-url');
  _liveStatusInterval: number = this.#parseLiveStatusInterval(
    this.getAttribute('live-status-interval'),
  );
  _isPlayable = false;
  _hasSeenLiveActive = false;
  _liveStatusTimer: ReturnType<typeof setTimeout> | null = null;
  _liveStatusInFlight = false;
  _visibilityHandler: EventListener | null = null;
  _hlsReloadRetryTimer: ReturnType<typeof setTimeout> | null = null;
  _contentSrc: string | null = this.#normalizeContentSrc(
    this.getAttribute('content-src'),
  );
  _rememberPositionEnabled: boolean = this.#parseRememberPositionAttribute(
    this.getAttribute('remember-position'),
  );
  _rememberPositionKey: string | null = this.#getRememberPositionStorageKey();
  _pendingRememberedPosition: number | null = null;
  _rememberPositionListenersAttached = false;
  _mediaErrorRecoveryAttempts = 0;

  /**
   * Parse DRM version attribute value to integer
   * @param {string|null} value - Attribute value to parse
   * @returns {number|null} Parsed integer or null if value is invalid/absent
   */
  #parseDrmVersion(value: string | null): number | null {
    return value ? parseInt(value, 10) : null;
  }

  #parseLiveStatusInterval(value: string | null): number {
    const parsed = value ? parseInt(value, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;
  }

  #getMediaController(): MediaControllerElement | null {
    if (typeof this.closest !== 'function') return null;
    const controller = this.closest('media-controller');
    return controller instanceof HTMLElement
      ? controller as MediaControllerElement
      : null;
  }

  #isLiveStreamController(): boolean {
    const controller = this.#getMediaController();
    return controller?.dataset?.liveStream === 'true';
  }

  #isLiveManagedSource(): boolean {
    return this.#isLiveStreamController() ||
      this.hasAttribute('live-status-url');
  }

  #shouldAutoSyncContentSrc(): boolean {
    return this.#isLiveManagedSource() ||
      (!this.hasAttribute('ad-tag-src') && !this.hasAttribute('vast-src'));
  }

  #normalizeContentSrc(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const normalizedContentSrc = value.trim();
    return normalizedContentSrc.length > 0 ? normalizedContentSrc : null;
  }

  #setControllerData(name: string, value: string | null | undefined): void {
    const controller = this.#getMediaController();
    if (!controller) return;
    if (value === null || value === undefined) {
      controller.removeAttribute(`data-${name}`);
      delete controller.dataset[name];
    } else {
      controller.setAttribute(`data-${name}`, String(value));
    }
  }

  #hasTrailerPosterSource(): boolean {
    const controller = this.#getMediaController();
    if (!controller) return false;
    const trailerPoster = controller.querySelector(
      'bunny-poster-video[slot="poster"]',
    );
    if (!(trailerPoster instanceof HTMLElement)) return false;
    const trailerSrc = trailerPoster.getAttribute('src');
    return typeof trailerSrc === 'string' && trailerSrc.trim().length > 0;
  }

  #syncTrailerActiveState(isLiveActive: boolean): void {
    if (isLiveActive) {
      this._hasSeenLiveActive = true;
    }

    const shouldShowTrailer = this.#hasTrailerPosterSource() &&
      !this._isPlayable &&
      !this._hasSeenLiveActive;

    this.#setControllerData(
      'trailer-active',
      shouldShowTrailer ? 'true' : null,
    );
  }
  #isDebugLoggingEnabled(): boolean {
    if (this.getAttribute('debug') === 'true') return true;
    try {
      return globalThis.localStorage?.getItem('bunny_player_debug') === 'true';
    } catch (_error) {
      return false;
    }
  }

  #debugLogUnexpectedError(
    context: string,
    error: unknown,
    expectedErrorNames: string[] = [],
  ): void {
    const errorName = error instanceof Error ? error.name : '';
    if (expectedErrorNames.includes(errorName)) return;
    if (!this.#isDebugLoggingEnabled()) return;
    console.debug(`[BunnyStreamVideo] ${context}`, error);
  }

  #clearHlsReloadRetryTimer(): void {
    if (this._hlsReloadRetryTimer) {
      clearTimeout(this._hlsReloadRetryTimer);
      this._hlsReloadRetryTimer = null;
    }
  }

  #scheduleHlsReloadRetry(delayMs: number): void {
    if (this._hlsReloadRetryTimer) return;
    this._hlsReloadRetryTimer = setTimeout(() => {
      this._hlsReloadRetryTimer = null;
      if (!this.isConnected || !this.api) return;
      this.api.startLoad();
    }, delayMs);
  }

  #isVodTransitionState(): boolean {
    const controller = this.#getMediaController();
    return this._isPlayable &&
      controller?.dataset?.liveStream === 'true' &&
      controller?.dataset?.liveActive !== 'true';
  }

  #getHlsErrorStatusCode(data: HlsErrorData | null | undefined): number | null {
    return data?.response?.code ??
      data?.response?.status ??
      data?.networkDetails?.status ??
      data?.networkDetails?.response?.status ??
      null;
  }

  #isManifest404Error(data: HlsErrorData | null | undefined): boolean {
    if (!data) return false;
    const isManifestLoadError =
      data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
      data.details === 'manifestLoadError';
    if (!isManifestLoadError) return false;
    return this.#getHlsErrorStatusCode(data) === 404;
  }

  #isVodTransition404RetryableError(
    data: HlsErrorData | null | undefined,
  ): boolean {
    if (!data) return false;
    if (this.#getHlsErrorStatusCode(data) !== 404) return false;

    return data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
      data.details === 'manifestLoadError' ||
      data.details === Hls.ErrorDetails.LEVEL_LOAD_ERROR ||
      data.details === 'levelLoadError' ||
      data.details === Hls.ErrorDetails.AUDIO_TRACK_LOAD_ERROR ||
      data.details === 'audioTrackLoadError';
  }

  #setMediaSrc(nextSrc: string | null): boolean {
    if (!nextSrc) return false;
    if (this.getAttribute('src') !== nextSrc) {
      this.#clearHlsReloadRetryTimer();
      this.setAttribute('src', nextSrc);
      return true;
    }
    return false;
  }

  #setContentSrc(nextSrc: string | null | undefined): boolean {
    const normalizedSrc = this.#normalizeContentSrc(nextSrc);
    if (this._contentSrc === normalizedSrc) return false;

    this._contentSrc = normalizedSrc;
    this.#syncCastSrc();
    return true;
  }

  #syncCastSrc(): void {
    const castSrc = this._contentSrc;

    if (castSrc) {
      if (this.getAttribute('cast-src') !== castSrc) {
        this.setAttribute('cast-src', castSrc);
      }
      return;
    }

    if (this.hasAttribute('cast-src')) {
      this.removeAttribute('cast-src');
    }
  }

  #updateCountdownState(
    scheduledTime: string | null,
    enableCountdown: boolean,
  ): void {
    const controller = this.#getMediaController();
    if (!controller) return;

    const countdown = controller.querySelector('live-stream-countdown');
    const shouldShow = Boolean(enableCountdown && scheduledTime);

    if (countdown) {
      if (shouldShow) {
        countdown.setAttribute('scheduled-time', scheduledTime ?? '');
      } else {
        countdown.removeAttribute('scheduled-time');
      }
    }

    this.#setControllerData('countdown', shouldShow ? 'true' : null);
  }

  #updateStatusOverlayState(shouldShow: boolean): void {
    this.#setControllerData('status', shouldShow ? 'true' : null);
  }

  #updateStatusMessage(statusMessage: string | null | undefined): void {
    const controller = this.#getMediaController();
    if (!controller) return;
    const statusMessageNode = controller.querySelector(
      '.offline-overlay[data-overlay="status"] .offline-message',
    );
    if (!(statusMessageNode instanceof HTMLElement)) return;
    statusMessageNode.textContent = statusMessage || '';
  }

  #updatePosterThumbnail(thumbnailUrl: string | null | undefined): void {
    if (!thumbnailUrl || typeof thumbnailUrl !== 'string') return;
    const controller = this.#getMediaController();
    if (!controller) return;

    const posterImage = controller.querySelector(
      'bunny-poster-image[slot="poster"]',
    );
    if (posterImage instanceof HTMLElement) {
      posterImage.setAttribute('src', thumbnailUrl);
    }

    // Trailer poster intentionally does not mirror thumbnail to avoid pre-stream thumbnail flashes.
  }

  #setControllerGesturesDisabled(disabled: boolean): void {
    const controller = this.#getMediaController();
    if (!controller || controller?.dataset?.liveStream !== 'true') return;
    if (disabled) {
      controller.setAttribute('gesturesdisabled', '');
    } else {
      controller.removeAttribute('gesturesdisabled');
    }
  }

  #syncContentSrcToRuntimeSrc(): boolean {
    const contentSrc = this._contentSrc;

    if (!this.#isLiveManagedSource()) {
      if (!contentSrc) return false;
      return this.#setMediaSrc(contentSrc);
    }

    if (this._isPlayable) {
      if (!contentSrc) return false;
      return this.#setMediaSrc(contentSrc);
    }

    if (this.hasAttribute('src')) {
      this.removeAttribute('src');
      return true;
    }

    return false;
  }

  #applyLiveState({
    isPlayable = false,
    isLiveActive = false,
    showCountdown = false,
    scheduledStartTime = null,
    showStatus = false,
    statusMessage,
    thumbnailUrl = null,
    allowAutoResume = true,
  }: LiveStateOptions = {}): void {
    const wasPlaying = !this.paused;
    this._isPlayable = Boolean(isPlayable);
    this.#setControllerData('live', this._isPlayable ? 'true' : null);
    this.#setControllerData('live-active', isLiveActive ? 'true' : null);
    this.#syncTrailerActiveState(isLiveActive);
    this.#updateCountdownState(scheduledStartTime, showCountdown);
    this.#updateStatusOverlayState(showStatus);
    if (statusMessage !== undefined && statusMessage !== null) {
      this.#updateStatusMessage(String(statusMessage));
    }
    this.#updatePosterThumbnail(thumbnailUrl);
    this.#setControllerGesturesDisabled(!this._isPlayable);

    if (this._isPlayable) {
      const sourceChanged = this.#syncContentSrcToRuntimeSrc();
      if (wasPlaying && sourceChanged && allowAutoResume) {
        this.play?.().catch((error) => {
          this.#debugLogUnexpectedError(
            'Failed to auto-resume playback after source update',
            error,
            ['NotAllowedError', 'AbortError'],
          );
        });
      }
      return;
    }

    const isOfflineLiveState = showCountdown || showStatus;
    if (isOfflineLiveState && this.hasAttribute('src')) {
      this.pause?.();
      this.removeAttribute('src');
    }
  }

  #getLiveStateSnapshot(): LiveStateOptions {
    const controller = this.#getMediaController();
    const countdown = controller?.querySelector('live-stream-countdown');
    const scheduledStartTime = countdown?.getAttribute('scheduled-time') ||
      null;

    return {
      isPlayable: controller?.dataset?.live === 'true',
      isLiveActive: controller?.dataset?.liveActive === 'true',
      showCountdown: controller?.dataset?.countdown === 'true',
      scheduledStartTime,
      showStatus: controller?.dataset?.status === 'true',
    };
  }

  #scheduleNextPoll(): void {
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
    }
    this._liveStatusTimer = setTimeout(() => {
      this._liveStatusTimer = null;
      this.#pollLiveStatus();
    }, this._liveStatusInterval);
  }

  #isNonRetryableStatus(status: number): boolean {
    return status === 401 || status === 403 || status === 404 || status === 410;
  }

  async #pollLiveStatus() {
    if (!this.isConnected) return;
    if (!this._liveStatusUrl) return;
    if (!this.#isLiveStreamController()) return;
    if (this._liveStatusInFlight) {
      this.#scheduleNextPoll();
      return;
    }
    this._liveStatusInFlight = true;
    try {
      const pollUrl = new URL(this._liveStatusUrl, globalThis.location.href);
      if (document.referrer) {
        pollUrl.searchParams.set('referer', document.referrer);
      }

      const response = await fetch(pollUrl.toString(), {
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json() as LiveStatusResponse;
        const liveSrcChanged = this.#setContentSrc(data?.videoPlaylistUrl);
        const isPlayable = data?.isPlayable === true;
        const isLiveActive = data?.isLiveActive === true;
        const showCountdown = data?.showCountdown === true;
        const scheduledStartTime = data?.scheduledStartTime || null;
        const showStatus = data?.showStatus === true;
        const statusMessage = data?.statusMessage || '';
        const thumbnailUrl = data?.thumbnailUrl || null;

        this.#applyLiveState({
          isPlayable,
          isLiveActive,
          showCountdown,
          scheduledStartTime,
          showStatus,
          statusMessage,
          thumbnailUrl,
          allowAutoResume: !(liveSrcChanged && isPlayable && !isLiveActive),
        });
      } else if (this.#isNonRetryableStatus(response.status)) {
        this.#stopLiveStatusPolling();
        return;
      }
    } catch (error) {
      // Transient network failures are expected; log only unexpected cases in debug mode.
      this.#debugLogUnexpectedError('Live status polling failed', error, [
        'AbortError',
        'TypeError',
      ]);
    } finally {
      this._liveStatusInFlight = false;
    }

    if (
      this.isConnected &&
      document.visibilityState === 'visible'
    ) {
      this.#scheduleNextPoll();
    }
  }

  #handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      if (this._liveStatusTimer) {
        clearTimeout(this._liveStatusTimer);
        this._liveStatusTimer = null;
      }
      this.#pollLiveStatus();
      return;
    }
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
      this._liveStatusTimer = null;
    }
  }

  #startLiveStatusPolling(): void {
    this.#stopLiveStatusPolling();
    if (!this._liveStatusUrl) return;
    if (!this.#isLiveStreamController()) return;

    if (
      !this._liveStatusTimer &&
      !this._liveStatusInFlight &&
      document.visibilityState === 'visible'
    ) {
      this.#scheduleNextPoll();
    }

    if (!this._visibilityHandler) {
      this._visibilityHandler = this.#handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  #stopLiveStatusPolling(): void {
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
      this._liveStatusTimer = null;
    }
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this.#clearHlsReloadRetryTimer();
  }

  #syncLiveState(): void {
    if (!this._liveStatusUrl) {
      this.#stopLiveStatusPolling();
      return;
    }
    const controller = this.#getMediaController();
    if (!controller || !this.#isLiveStreamController()) {
      this.#stopLiveStatusPolling();
      return;
    }
    this.#applyLiveState(this.#getLiveStateSnapshot());
    this.#startLiveStatusPolling();
  }

  /**
   * Check if preload is enabled based on preload attribute
   * HTML5 preload values: "auto" (load entire video), "metadata" (load metadata only), "none" (no preload)
   * @returns {boolean} True if preload is "auto", false otherwise (none or metadata)
   */
  #shouldPreload(): boolean {
    const preloadAttr = this.getAttribute('preload');
    return preloadAttr === 'auto';
  }

  #syncWebkitPlaysinline(): void {
    const shouldUseWebkitPlaysinline =
      this.hasAttribute('disable-ios-player') ||
      this.hasAttribute('playsinline');

    if (shouldUseWebkitPlaysinline) {
      if (!this.hasAttribute('webkit-playsinline')) {
        this.setAttribute('webkit-playsinline', '');
      }
      return;
    }

    this.removeAttribute('webkit-playsinline');
  }

  #parseRememberPositionAttribute(value: string | null): boolean {
    if (value === null) return false;
    return value !== 'false';
  }

  #getRememberPositionStorageKey(): string | null {
    const videoId = this.getAttribute('video-id');
    return videoId ? `bunny_player_position_${videoId}` : null;
  }

  #readRememberedPosition(storageKey: string | null): number | null {
    if (!storageKey) return null;

    try {
      const rawValue = globalThis.localStorage?.getItem(storageKey);
      if (!rawValue) return null;

      const parsed = Number.parseFloat(rawValue);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (_error) {
      return null;
    }
  }

  #writeRememberedPosition(): void {
    if (!this._rememberPositionEnabled || !this._rememberPositionKey) return;
    if (!Number.isFinite(this.currentTime)) return;

    try {
      globalThis.localStorage?.setItem(
        this._rememberPositionKey,
        this.currentTime.toString(),
      );
    } catch (_error) {
      // Ignore storage failures such as private browsing restrictions.
    }
  }

  #clearRememberedPosition(): void {
    if (!this._rememberPositionKey) return;

    try {
      globalThis.localStorage?.removeItem(this._rememberPositionKey);
    } catch (_error) {
      // Ignore storage failures such as private browsing restrictions.
    }
  }

  #restoreRememberedPosition(): void {
    if (!this._rememberPositionEnabled) return;
    const rememberedPosition = this._pendingRememberedPosition;
    if (rememberedPosition == null || !Number.isFinite(rememberedPosition)) {
      return;
    }

    const duration = Number.isFinite(this.duration) ? this.duration : null;
    let nextTime = rememberedPosition;

    if (duration !== null) {
      nextTime = Math.min(nextTime, Math.max(duration - 1, 0));
    }

    this.currentTime = Math.max(nextTime, 0);
    this._pendingRememberedPosition = null;
  }

  #syncRememberPositionState(): void {
    this._rememberPositionEnabled = this.#parseRememberPositionAttribute(
      this.getAttribute('remember-position'),
    );
    this._rememberPositionKey = this.#getRememberPositionStorageKey();

    if (!this._rememberPositionEnabled || !this._rememberPositionKey) {
      this._pendingRememberedPosition = null;
      return;
    }

    this._pendingRememberedPosition = this.#readRememberedPosition(
      this._rememberPositionKey,
    );

    if (this.readyState > 0) {
      this.#restoreRememberedPosition();
    }
  }

  #attachRememberPositionListeners(): void {
    if (this._rememberPositionListenersAttached) return;

    this.addEventListener('loadedmetadata', () => {
      this.#restoreRememberedPosition();
    });
    this.addEventListener('timeupdate', () => {
      this.#writeRememberedPosition();
    });
    this.addEventListener('ended', () => {
      this.#clearRememberedPosition();
    });

    this._rememberPositionListenersAttached = true;
  }

  override attributeChangedCallback(
    name: string,
    oldVal: string | null,
    newVal: string | null,
  ): void {
    if (name === 'content-src') {
      const contentSrcChanged = this.#setContentSrc(newVal);
      if (contentSrcChanged) {
        if (this.#shouldAutoSyncContentSrc()) {
          if (this.#isLiveManagedSource()) {
            if (this.isConnected) {
              this.#applyLiveState(this.#getLiveStateSnapshot());
            }
          } else {
            this.#syncContentSrcToRuntimeSrc();
          }
        }
      }
    }
    if (name === 'disable-ios-player') {
      this.#syncWebkitPlaysinline();
    }
    if (name === 'remember-position' || name === 'video-id') {
      this.#syncRememberPositionState();
    }
    if (name === 'drm-version') {
      this._drmVersion = this.#parseDrmVersion(newVal);
      this._edrmEnabled = this._drmVersion === 1;
    }
    if (name === 'video-id') this._videoId = newVal;
    if (name === 'library-id') this._libraryId = newVal;
    if (name === 'drm-engine') this._drmEngine = newVal;
    if (name === 'base-url') {
      this._baseUrl = newVal || 'https://video.bunnycdn.com';
    }
    if (name === 'drm-provider') this._drmProvider = newVal;
    if (name === 'is-jit') {
      this.isJit = newVal === 'true';
    }
    if (name === 'level-cap') {
      this._levelCap = this.getAttribute('level-cap') === 'true';
    }
    if (name === 'start-time') {
      this._startTime = parseFloat(newVal || '0');
    }
    if (name === 'chromecast') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'airplay') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'live-status-url') {
      this._liveStatusUrl = newVal;
    }
    if (name === 'live-status-interval') {
      this._liveStatusInterval = this.#parseLiveStatusInterval(newVal);
    }
    super.attributeChangedCallback?.(name, oldVal, newVal);

    if (
      name === 'live-status-url' ||
      name === 'live-status-interval'
    ) {
      this.#syncLiveState();
    }
  }

  constructor() {
    super();
    this.mediaSourceSupported = this.#checkMediaSourceSupport();
    this.isJit = false;

    this._edrmEnabled = this._drmVersion === 1;

    this.#updateDisableRemotePlayback();

    // Make element focusable for keyboard events
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Add keyboard event listeners for volume control
    this.addEventListener('keydown', this.#handleKeyDown.bind(this));
  }

  override connectedCallback(): void {
    super.connectedCallback?.();
    this.#syncCastSrc();
    this.#attachRememberPositionListeners();
    this.#syncWebkitPlaysinline();
    this.#syncRememberPositionState();
    this.#syncLiveState();
    if (!this.#isLiveManagedSource() && this.#shouldAutoSyncContentSrc()) {
      this.#syncContentSrcToRuntimeSrc();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#stopLiveStatusPolling();
    this.#clearHlsReloadRetryTimer();
  }

  override init(): void {
    super.init();
    // Ensure shadow root exists (HlsVideoElement likely creates it, but confirm)
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }

    // When preload is disabled, HLS.js won't start loading automatically (autoStartLoad: false)
    // We need to start loading when the user initiates playback
    if (!this.#shouldPreload()) {
      // Start loading when play is triggered (added early to prevent race conditions)
      this.addEventListener('play', () => {
        if (this.api) {
          this.api.startLoad();
        }
      }, { once: true });
    }
  }

  /**
   * Detect the maximum available Widevine robustness level supported by the browser
   * @returns {Promise<string>} The highest supported robustness level
   */
  async #detectMaxRobustness(): Promise<string> {
    // Robustness levels to test in priority order (L1 hardware -> L3 software)
    const robustnessLevels = [
      'HW_SECURE_CRYPTO', // L1: Hardware-backed crypto
      'HW_SECURE_DECODE', // L1: Hardware-backed decode
      'SW_SECURE_CRYPTO', // L3: Software-based
    ];

    // Test each robustness level using EME API
    for (const level of robustnessLevels) {
      try {
        const config = [{
          initDataTypes: ['cenc'],
          videoCapabilities: [{
            contentType: 'video/mp4; codecs="avc1.42E01E"',
            robustness: level,
          }],
        }];

        await navigator.requestMediaKeySystemAccess(
          'com.widevine.alpha',
          config,
        );
        console.debug(`Detected max Widevine robustness: ${level}`);
        return level;
      } catch (_error) {
        // This level not supported, try next
        continue;
      }
    }

    // Fallback to software-based if detection fails
    console.debug(
      'Widevine robustness detection failed, using SW_SECURE_CRYPTO',
    );
    return 'SW_SECURE_CRYPTO';
  }

  async #getRobustness(): Promise<{ videoRobustness: string }> {
    const drmRobustness = this.getAttribute('drm-robustness');

    // If backend provides a minimum robustness level, respect it
    if (drmRobustness) {
      switch (+drmRobustness) {
        case 1:
          return {
            videoRobustness: 'HW_SECURE_DECODE',
          };
        case 2:
          return {
            videoRobustness: 'HW_SECURE_CRYPTO',
          };
        case 3:
          return {
            videoRobustness: 'SW_SECURE_CRYPTO',
          };
      }
    }

    // No backend setting: detect and use maximum available robustness
    const maxRobustness = await this.#detectMaxRobustness();
    return {
      videoRobustness: maxRobustness,
    };
  }

  #getManagedMediaSource(): typeof MediaSource | null {
    return (
      globalThis as typeof globalThis & {
        ManagedMediaSource?: typeof MediaSource;
      }
    ).ManagedMediaSource ?? null;
  }

  #checkMediaSourceSupport(): boolean {
    try {
      // Check for both MediaSource (desktop) and ManagedMediaSource (iOS 17.1+)
      return typeof MediaSource !== 'undefined' ||
        this.#getManagedMediaSource() !== null;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Get the appropriate MediaSource API for codec support checking
   * @returns {typeof MediaSource | null} MediaSource or ManagedMediaSource API, or null if unavailable
   */
  #getMediaSourceAPI(): MediaSourceLike | null {
    if (typeof MediaSource !== 'undefined') return MediaSource;
    return this.#getManagedMediaSource();
  }

  /**
   * Check if the current page is loaded over HTTPS
   * @returns {boolean} True if HTTPS, false otherwise
   */
  #isSecureContext(): boolean {
    return globalThis.location?.protocol === 'https:' ||
      globalThis.location?.hostname === 'localhost' ||
      globalThis.location?.hostname === '127.0.0.1';
  }

  /**
   * Dispatch a player error event that can be caught by media-chrome or other listeners
   * @param {string} errorType - Type of error (e.g., 'drm', 'network', 'media')
   * @param {string} errorCode - Error code for translation lookup
   * @param {string} message - Human-readable error message
   * @param {object} details - Additional error details
   */
  #dispatchPlayerError(
    errorType: string,
    errorCode: string,
    message: string,
    details: Record<string, unknown> = {},
  ): void {
    const errorEvent = new CustomEvent('playererror', {
      bubbles: true,
      composed: true,
      detail: {
        type: errorType,
        code: errorCode,
        message,
        ...details,
      },
    });
    this.dispatchEvent(errorEvent);

    // Also set the error on the video element for native error handling
    console.error(`[Player Error] ${errorType}: ${message}`, details);
  }

  /**
   * Check if a HLS.js error is DRM-related
   * @param {string} errorDetails - The error details from HLS.js
   * @returns {boolean} True if DRM-related error
   */
  #isDrmError(errorDetails: string): boolean {
    return DRM_ERROR_DETAILS.includes(errorDetails);
  }

  /**
   * Use native HLS FairPlay on iOS/iPadOS. The server can miss iPad desktop-mode
   * UAs because they look like macOS Safari, so keep a runtime guard here too.
   */
  #shouldUseInternalFairPlay(): boolean {
    if (!this._edrmEnabled) return false;
    if (this._drmEngine === 'internal') return true;
    return this._drmProvider === 'fairplay' && isIOS();
  }

  /**
   * Update disableRemotePlayback based on chromecast and airplay attributes and browser type
   */
  #updateDisableRemotePlayback(): void {
    const chromecastDisabled = this.getAttribute('chromecast') !== 'true' &&
      isChrome();
    const airplayDisabled = this.getAttribute('airplay') === 'false' &&
      (isSafari() || isIOS());

    this.disableRemotePlayback = chromecastDisabled || airplayDisabled;
  }

  /**
   * Get the underlying HTMLVideoElement
   * @returns {HTMLVideoElement|null} The video element or null if not found
   */
  #getVideoElement(): HTMLVideoElement | null {
    // Check HLS.js api media reference first (most reliable)
    if (this.api && this.api.media) {
      return this.api.media instanceof HTMLVideoElement ? this.api.media : null;
    }

    // Fallback to shadow DOM
    if (this.shadowRoot) {
      const videoInShadow = this.shadowRoot.querySelector('video');
      if (videoInShadow) return videoInShadow;
    }

    // Last resort: direct child
    return this.querySelector('video');
  }

  /**
   * Setup start time seeking when video metadata loads using media-chrome seek request
   */
  #setupStartTimeSeeking(): void {
    const seekToStartTime = () => {
      this.dispatchEvent(
        new CustomEvent('mediaseekrequest', {
          composed: true,
          bubbles: true,
          detail: this._startTime,
        }),
      );
    };

    // If video is already ready, seek immediately
    if (this.readyState >= 1) {
      seekToStartTime();
    } else {
      // Wait for metadata to load
      this.addEventListener('loadedmetadata', seekToStartTime, { once: true });
    }
  }

  /**
   * Handle keyboard events for volume control
   */
  #handleKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.volume = Math.min(1, this.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.volume = Math.max(0, this.volume - 0.1);
        break;
    }
  }

  /**
   * Apply codec filtering to HLS levels (for UI purposes only)
   * Unlike the legacy player, we don't remove levels from hls.js to preserve ABR functionality
   */
  #applyCodecFilteringForUI(levels: CodecFilterCandidate[]): void {
    if (!Array.isArray(levels) || !this.mediaSourceSupported) return;

    // Get the correct MediaSource API (MediaSource on desktop, ManagedMediaSource on iOS 17.1+)
    const mediaSourceAPI = this.#getMediaSourceAPI();
    const codecLevels = levels as Parameters<typeof getBestCodecType>[0];

    // Determine the best codec type to keep
    const bestCodecType = getBestCodecType(
      codecLevels,
      mediaSourceAPI ?? undefined,
    );

    if (!bestCodecType) {
      return; // Keep all levels
    }

    // Filter levels to preferred codec while preserving original indices for HLS.js switching
    const levelsToKeep = levels
      .map((level, index) => ({ ...level, originalIndex: index }))
      .filter((level) => {
        const codecSet = level.codecSet || '';
        return codecSet.includes(bestCodecType) &&
          isCodecSupported(
            level as unknown as Parameters<typeof isCodecSupported>[0],
            mediaSourceAPI ?? undefined,
          );
      });

    this._filteredLevels = levelsToKeep;

    // Communicate filtered levels to the rendition menu
    const rootNode = this.getRootNode();
    if (
      !(
        rootNode instanceof Document ||
        rootNode instanceof ShadowRoot ||
        rootNode instanceof Element
      )
    ) {
      return;
    }

    const renditionMenu = rootNode.querySelector(
      'bunny-rendition-menu',
    ) as RenditionMenuElement | null;
    if (renditionMenu && 'filteredLevels' in renditionMenu) {
      renditionMenu.filteredLevels = levelsToKeep;
    }
  }

  /**
   * Remove HEVC levels from HLS.js on Gecko-based browsers with broken HEVC seeking.
   * Unlike the UI-only filtering above, this actually removes levels from HLS.js
   * so ABR cannot switch to them. Returns false if the stream is HEVC-only and the
   * browser cannot handle it (caller should show an error).
   */
  #removeUnsupportedHevcLevels(
    hls: Hls,
    levels: CodecFilterCandidate[],
  ): boolean {
    if (!isGecko() || !Array.isArray(levels) || levels.length === 0) {
      return true;
    }

    const hevcIndices: number[] = [];
    let nonHevcCount = 0;

    for (let i = 0; i < levels.length; i++) {
      const codecSet = (levels[i].codecSet || '').toLowerCase();
      if (codecSet.includes('hvc1') || codecSet.includes('hev1')) {
        hevcIndices.push(i);
      } else {
        nonHevcCount++;
      }
    }

    if (hevcIndices.length === 0) return true;

    // HEVC-only stream on Firefox — no fallback available
    if (nonHevcCount === 0) return false;

    // Remove HEVC levels in reverse order to preserve indices
    for (let i = hevcIndices.length - 1; i >= 0; i--) {
      hls.removeLevel(hevcIndices[i]);
    }

    return true;
  }

  #pickStartLevelForPlayerSize(
    levels: Array<{ width?: number; height?: number }>,
  ): number {
    return pickStartLevelForPlayerSize(
      levels,
      this.offsetWidth,
      this.offsetHeight,
      this._levelCap,
      globalThis.devicePixelRatio ?? 1,
    );
  }

  generateDrmRequest(
    initDataType: string,
    initData: BufferSource,
    _keyContext: unknown,
  ): { initDataType: string; initData: BufferSource } {
    return { initDataType, initData };
  }

  /**
   * Get token query parameters from URL
   * @returns {string} Query string with token and expires params, or empty string
   */
  #getTokenQueryParams(): string {
    const url = new URL(globalThis.location.href);
    const token = url.searchParams.get('token') || '';
    const expires = parseInt(url.searchParams.get('expires') || '0', 10);

    if (token && expires) {
      return `token=${token}&expires=${expires}`;
    }
    return '';
  }

  /**
   * Get token query parameters with leading ? for URL construction
   * @returns {string} Query string with ? prefix, or empty string
   */
  #getTokenQueryString(): string {
    const params = this.#getTokenQueryParams();
    return params ? `?${params}` : '';
  }

  /**
   * Build FairPlay license URL
   * @returns {string} FairPlay license URL
   */
  #getFairPlayLicenseUrl(): string {
    const queryParams = this.#getTokenQueryParams();
    const separator = queryParams ? '&' : '';
    return `${this._baseUrl}/FairPlay/${this._libraryId}/license?videoId=${this._videoId}${separator}${queryParams}`;
  }

  /**
   * Build FairPlay certificate URL
   * @returns {string} FairPlay certificate URL
   */
  #getFairPlayCertificateUrl(): string {
    return `${this._baseUrl}/FairPlay/${this._libraryId}/certificate`;
  }

  /**
   * Build Widevine license URL
   * @returns {string} Widevine license URL
   */
  #getWidevineLicenseUrl(): string {
    const queryString = this.#getTokenQueryString();
    return `${this._baseUrl}/WidevineLicense/${this._libraryId}/${this._videoId}${queryString}`;
  }

  override async load(): Promise<void> {
    const nativeEl = this.nativeEl;

    const preloadAttr = this.getAttribute('preload');
    if (nativeEl && preloadAttr) {
      nativeEl.preload = preloadAttr as '' | 'none' | 'metadata' | 'auto';
    }

    // Non-HLS source (e.g. original MP4): bypass HLS.js and use native video directly.
    // HLS.js would try to parse the file as an HLS manifest and fail in a retry loop.
    if (this.getAttribute('use-hlsjs') === 'false') {
      if (nativeEl && this.src) {
        nativeEl.src = this.src;
      }
      if (this._startTime > 0) {
        this.#setupStartTimeSeeking();
      }
      return;
    }

    const shouldPreload = this.#shouldPreload();
    const useInternalFairPlay = this.#shouldUseInternalFairPlay();

    // Start fresh with basic HLS config
    const basicHlsConfig: Record<string, unknown> = {
      debug: false,
      emeEnabled: this._edrmEnabled && !useInternalFairPlay,
      enableWorker: true,
      autoStartLoad: shouldPreload,
      maxMaxBufferLength: 120,
      maxBufferLength: 120,
      // Keep this at one segment length so ABR can upgrade at the start of the
      // second segment rather than waiting two full segments (8 s) before the
      // first quality switch fires.
      minBufferLength: 4,
      abrEwmaDefaultEstimate: 5000000,
      // Feed actual measured fragment download rates back into the EWMA
      // estimator so bandwidth converges to reality faster.
      abrMaxWithRealBitrate: true,
      startLevel: -1,
    };

    if (this._levelCap) {
      basicHlsConfig.capLevelToPlayerSize = true;
      basicHlsConfig.ignoreDevicePixelRatio = true;
    }

    // JIT segments may 404 briefly while the routing script is still encoding
    // them on demand. Tolerate that with long, linear-backoff retries instead
    // of bubbling up a fatal network error. Must be set on the config before
    // super.load() so hls.js picks it up for the very first fragment request.
    if (this.isJit) {
      basicHlsConfig.fragLoadPolicy = {
        default: {
          maxTimeToFirstByteMs: 10000,
          maxLoadTimeMs: 120000,
          timeoutRetry: {
            maxNumRetry: 4,
            retryDelayMs: 0,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 100,
            retryDelayMs: 1000,
            maxRetryDelayMs: 1000,
            backoff: 'linear',
          },
        },
      };
    }

    // Don't spread existing config - start fresh
    this.config = basicHlsConfig;

    // Only configure DRM if drmVersion is 1
    if (this._edrmEnabled) {
      if (useInternalFairPlay) {
        // Create StreamDrmEngine for native HLS FairPlay playback.
        const streamDrmEngine = new StreamDrmEngine(
          this.#getFairPlayLicenseUrl(),
          this.#getFairPlayCertificateUrl(),
          0,
        );
        this._streamDrmEngine = streamDrmEngine;
      } else {
        // Configure HLS.js DRM (modern Safari 17+, Chrome, Firefox, etc.)
        const isFairPlay = isFairPlayBrowser();

        // Only configure the DRM system for the current browser
        // Safari/iOS = FairPlay only, Chrome/Firefox/etc = Widevine only
        const drmSystems = isFairPlay
          ? {
            'com.apple.fps': {
              licenseUrl: this.#getFairPlayLicenseUrl(),
              serverCertificateUrl: this.#getFairPlayCertificateUrl(),
            },
          }
          : {
            'com.widevine.alpha': {
              licenseUrl: this.#getWidevineLicenseUrl(),
              generateRequest: this.generateDrmRequest,
            },
          };

        const hlsDrmConfig = {
          emeEnabled: true,
          drmSystemOptions: isFairPlay ? {} : await this.#getRobustness(),
          drmSystems,
        };

        // Merge DRM config with basic config
        this.config = {
          ...this.config,
          ...hlsDrmConfig,
        };
      }
    }

    // For native HLS FairPlay, HLS.js via MSE/MMS is bypassed so WebKit owns
    // both the playlist and encrypted media pipeline.
    if (useInternalFairPlay) {
      // Guard against double initialization - load() can be called multiple times
      if (this._internalDrmInitialized) {
        return;
      }
      this._internalDrmInitialized = true;

      try {
        // Get the native video element directly without initializing HLS.js
        const videoEl = this.nativeEl;
        if (!videoEl) {
          throw new Error(
            'Could not find video element for native HLS playback',
          );
        }

        // Wire up error handling from DRM engine
        // For native HLS path, dispatch on document since the element may not be in
        // the standard media-chrome shadow DOM tree
        const streamDrmEngine = this._streamDrmEngine;
        if (!streamDrmEngine) {
          throw new Error('FairPlay DRM engine is not initialized');
        }

        streamDrmEngine.onError = (code: string, message: string) => {
          const errorEvent = new CustomEvent('playererror', {
            bubbles: true,
            composed: true,
            detail: {
              type: 'drm',
              code,
              message,
              details: code,
            },
          });
          document.dispatchEvent(errorEvent);
          console.error(`[Player Error] drm: ${message}`, { details: code });
        };

        // Initialize FairPlay DRM before setting source
        await streamDrmEngine.initFairPlay(videoEl);

        // Set source directly for native HLS playback (required for FairPlay)
        videoEl.src = this.src;

        // Handle start time seeking
        if (this._startTime > 0) {
          this.#setupStartTimeSeeking();
        }
      } catch (error) {
        this._internalDrmInitialized = false; // Allow retry on error
        throw error;
      }

      return;
    }

    // Call parent load (uses HLS.js) for all other cases
    await super.load();

    // Handle start time seeking
    if (this._startTime > 0) {
      this.#setupStartTimeSeeking();
    }

    // Only proceed if we have HLS.js support and an api instance
    const hls = this.api;
    if (!hls || !this.src) {
      return;
    }

    // Bind HLS.js events
    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      this.#clearHlsReloadRetryTimer();
      this._mediaErrorRecoveryAttempts = 0;

      const levels = data.levels || [];

      // On Firefox, remove HEVC levels from HLS.js to prevent broken seeking.
      // If the stream is HEVC-only, show an error since Firefox cannot handle it.
      if (!this.#removeUnsupportedHevcLevels(hls, levels)) {
        this.#dispatchPlayerError(
          'playback',
          'Codec Not Supported',
          'This video uses a codec (HEVC) that is not supported by your browser. Please try a different browser such as Chrome or Safari.',
          { codec: 'hevc', browser: 'firefox' },
        );
        hls.destroy();
        return;
      }

      this.#applyCodecFilteringForUI(hls.levels || []);

      // Jump directly to the highest quality that fits the player's display
      // size rather than starting at the bottom and ABR-ing up. Uses the
      // post-HEVC-removal level list so indices are stable.
      const startLevel = this.#pickStartLevelForPlayerSize(hls.levels ?? []);
      if (startLevel >= 0) {
        hls.startLevel = startLevel;
      }
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      const shouldRetryVodTransition404 =
        this.#isVodTransition404RetryableError(
          data,
        ) &&
        this.#isVodTransitionState();

      if (shouldRetryVodTransition404) {
        // Avoid rapid internal retry loops while VOD playlist is still being published.
        hls.stopLoad();
        this.#scheduleHlsReloadRetry(
          Math.max(this._liveStatusInterval, 5000),
        );
      }

      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (shouldRetryVodTransition404) {
            break;
          }
          if (this.#isManifest404Error(data) && this.#isVodTransitionState()) {
            this.#scheduleHlsReloadRetry(
              Math.max(this._liveStatusInterval, 5000),
            );
            break;
          }
          console.debug('fatal network error encountered, trying to recover');
          hls.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          this._mediaErrorRecoveryAttempts++;
          if (this._mediaErrorRecoveryAttempts <= 3) {
            console.debug(
              `fatal media error encountered, recovery attempt ${this._mediaErrorRecoveryAttempts}/3`,
            );
            hls.recoverMediaError();
          } else {
            console.debug(
              'fatal media error: max recovery attempts reached',
            );
            this.#dispatchPlayerError(
              'playback',
              'Source Not Supported',
              'An error occurred during playback. The media could not be loaded.',
              {
                details: data.details,
                reason: data.reason,
              },
            );
            hls.destroy();
          }
          break;
        default: {
          console.debug('unrecoverable error', data);
          const errorDetails = data.details || '';

          // Check if this is a DRM-related error
          if (this.#isDrmError(errorDetails)) {
            // Check if running on HTTP (non-secure context)
            if (!this.#isSecureContext()) {
              this.#dispatchPlayerError(
                'drm',
                'DRM Requires HTTPS',
                'This video requires a secure connection (HTTPS) to play DRM-protected content.',
                {
                  details: data.details,
                  reason: data.reason,
                  isSecure: false,
                },
              );
            } else {
              // DRM error on HTTPS - use legacy-style message
              this.#dispatchPlayerError(
                'drm',
                'Encryption Error',
                this.#getDrmErrorMessage(errorDetails),
                {
                  details: data.details,
                  reason: data.reason,
                },
              );
            }
          } else {
            // Generic unrecoverable error
            this.#dispatchPlayerError(
              'playback',
              'Source Not Supported',
              'An error occurred during playback. The media could not be loaded.',
              {
                details: data.details,
                reason: data.reason,
              },
            );
          }

          hls.destroy();
          break;
        }
      }
    });
  }

  /**
   * Get a human-readable error message for DRM errors
   * Messages match the legacy player for consistency
   * @param {string} errorDetails - The HLS.js error details
   * @returns {string} Human-readable error message
   */
  #getDrmErrorMessage(errorDetails: string): string {
    switch (errorDetails) {
      case 'keySystemNoAccess':
        // Legacy message: "This video cannot be played on your device because it doesn't meet the content protection requirements."
        return "This video cannot be played on your device because it doesn't meet the content protection requirements.";
      case 'keySystemStatusOutputRestricted':
        return "This video cannot be played on your device because it doesn't meet the content protection requirements.";
      case 'keySystemNoKeys':
      case 'keySystemNoSession':
      case 'keySystemNoConfiguredLicense':
      case 'keySystemLicenseRequestFailed':
      case 'keySystemServerCertificateRequestFailed':
      case 'keySystemServerCertificateUpdateFailed':
      case 'keySystemSessionUpdateFailed':
      case 'keySystemStatusInternalError':
      case 'keyLoadError':
      case 'keyLoadTimeOut':
      case 'keySystemError':
      default:
        // Legacy message: "There was an error with the content protection system."
        return 'There was an error with the content protection system.';
    }
  }
}

customElements.define('bunny-stream-video', BunnyStreamVideoElement);

export default BunnyStreamVideoElement;
